import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Phone, Mail, Calendar as CalendarIcon, FileText, CheckSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ActivityDialog } from "../../crm/ActivityDialog";

interface DealActivitiesProps {
  dealId: string;
  contactId: string | null;
}

const activityIcons = {
  call: Phone,
  email: Mail,
  meeting: CalendarIcon,
  note: FileText,
  task: CheckSquare,
};

export function DealActivities({ dealId, contactId }: DealActivitiesProps) {
  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [contact, setContact] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchActivities();
    if (contactId) {
      fetchContact();
    }
  }, [dealId, contactId]);

  const fetchContact = async () => {
    if (!contactId) return;
    
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select("*")
        .eq("id", contactId)
        .single();

      if (error) throw error;
      setContact(data);
    } catch (error) {
      console.error("Error fetching contact:", error);
    }
  };

  const fetchActivities = async () => {
    if (!contactId) {
      setActivities([]);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("activities")
        .select("*")
        .eq("contact_id", contactId)
        .order("activity_date", { ascending: false });

      if (error) throw error;
      setActivities(data || []);
    } catch (error) {
      console.error("Error fetching activities:", error);
      toast({
        title: "Error",
        description: "Failed to load activities",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!contactId) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No contact linked to this deal. Link a contact to track activities.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Activity Timeline</h3>
        <Button onClick={() => setIsAddingActivity(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Activity
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading activities...</div>
      ) : activities.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          No activities recorded yet. Add your first activity to track interactions.
        </Card>
      ) : (
        <div className="space-y-3">
          {activities.map((activity) => {
            const Icon = activityIcons[activity.activity_type as keyof typeof activityIcons] || FileText;
            
            return (
              <Card key={activity.id} className="p-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="font-semibold capitalize">{activity.activity_type}</h4>
                      <span className="text-sm text-muted-foreground">
                        {new Date(activity.activity_date).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="font-medium mb-1">{activity.subject}</p>
                    {activity.description && (
                      <p className="text-sm text-muted-foreground">
                        {activity.description}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <ActivityDialog
        open={isAddingActivity}
        onOpenChange={setIsAddingActivity}
        contact={contact}
      />
    </div>
  );
}
